import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CancelButtonSmall,
  EditButton,
  PrimaryButtonMedium,
} from "../../../../../components/Buttons/Buttons";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { RadioButton } from "../../../../../components/RadioButton/RadioButton";
import { H3 } from "../../../../../components/Typography/Typography";
import { Form, RadioButtonContainer } from "../../../../../layout/FormLayout";
import { BorderCard } from "../../../../../layout/portalPageLayout";
import { useFormWrapper } from "../../../../../util/util";
import {
  SettingsDescription,
  SettingsHeader,
  TextContainer,
} from "./PorfolioControlsTab";
import type { AxiosError } from "axios";
import { endpoints } from "../../../../../endpoints";
import axios from "axios";
import useSWR from "swr";
import type {
  LeadTransactionVisibility,
  StorefrontMetaData,
} from "../../../../../types/types";
import { Store } from "../../../../../Store";
import type { IStore } from "../../../../../Store";
import styled from "styled-components";

const Wrapper = styled.div`
  margin: ${({ theme }) => theme.resolve("spacing_2xl")} 0;
`;

const SampleRequestStatus = styled.span`
 font-size: ${({ theme }) => theme.fontSizes.medium}
 color: ${({ theme }) => theme.colors.darkText};
`;

type SampleAndQuoteRequestSettingsFormOutput = {
  sample_requests_visibility: LeadTransactionVisibility;
  quote_requests_visibility: LeadTransactionVisibility;
};

export const SampleAndQuoteRequestSettings = () => {
  const [editMode, setEditMode] = useState(false);
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useContext(Notifications);

  const {
    storeState: { storefront_id, storefront_metadata },
    storeDispatch,
  } = useContext<IStore>(Store);

  const { sample_requests_visibility, quote_requests_visibility } =
    storefront_metadata;

  const { handleSubmit, register } = useFormWrapper({
    shouldUnregister: false,
    defaultValues: {
      sample_requests_visibility,
      quote_requests_visibility,
    },
  });

  const { mutate: storeFrontMetadataMutate } = useSWR<
    StorefrontMetaData,
    AxiosError<StorefrontMetaData>
  >(endpoints.v1_storefronts_id_metadata(storefront_id));

  const onCancel = () => setEditMode(false);

  const onSubmit = async (values: SampleAndQuoteRequestSettingsFormOutput) => {
    try {
      await axios.patch(
        endpoints.v1_storefronts_id_metadata(storefront_id),
        values
      );
      storeDispatch({
        type: "SET_STOREFRONT_METADATA",
        payload: {
          ...storefront_metadata,
          quote_requests_visibility: values.quote_requests_visibility,
          sample_requests_visibility: values.sample_requests_visibility,
        },
      });

      notifySuccess(t("Changes saved successfully"));
      const metadata = await storeFrontMetadataMutate();
      if (metadata) {
        storeDispatch({
          type: "SET_STOREFRONT_METADATA",
          payload: metadata,
        });
      }
      setEditMode(false);
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("Could not update settings. Please try again later."),
        {
          error,
        }
      );
    }
  };

  const getHumanReadableTextFromUnion = new Map<
    LeadTransactionVisibility,
    string
  >([
    ["hidden", t("Hidden everywhere")],
    ["disabled", t("Disabled outside login")],
    ["enabled", t("Enabled everywhere")],
  ]);

  return (
    <Wrapper>
      <BorderCard>
        <SettingsHeader>
          <TextContainer>
            <H3 style={{ margin: 0 }}>
              {t("Sample & Quote Request Configuration")}
            </H3>
            <SettingsDescription>
              {t("Configure who can request quote & sample requests")}
            </SettingsDescription>
          </TextContainer>
          {!editMode && (
            <EditButton
              testid={"edit-lead-visibility"}
              onClick={() => setEditMode(true)}
            />
          )}
          {editMode && <CancelButtonSmall onClick={onCancel} />}
        </SettingsHeader>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <TextContainer style={{ width: "50%" }}>
              <SettingsDescription>{t("Sample Requests")}</SettingsDescription>
              {editMode ? (
                <RadioButtonContainer>
                  <RadioButton
                    name={"sample_requests_visibility"}
                    value="enabled"
                    theref={register({ required: true })}
                    optionTitle={t("Enabled everywhere")}
                  />
                  <RadioButton
                    name={"sample_requests_visibility"}
                    value="disabled"
                    theref={register({ required: true })}
                    optionTitle={t("Disabled outside login")}
                  />
                  <RadioButton
                    name={"sample_requests_visibility"}
                    value="hidden"
                    theref={register({ required: true })}
                    optionTitle={t("Hidden everywhere")}
                  />
                </RadioButtonContainer>
              ) : (
                <SampleRequestStatus>
                  {getHumanReadableTextFromUnion.get(
                    sample_requests_visibility
                  )}
                </SampleRequestStatus>
              )}
            </TextContainer>
            <TextContainer style={{ width: "50%" }}>
              <SettingsDescription>{t("Quote Requests")}</SettingsDescription>
              {editMode ? (
                <RadioButtonContainer>
                  <RadioButton
                    name={"quote_requests_visibility"}
                    value="enabled"
                    theref={register({ required: true })}
                    optionTitle={t("Enabled everywhere")}
                  />
                  <RadioButton
                    name={"quote_requests_visibility"}
                    value="disabled"
                    theref={register({ required: true })}
                    optionTitle={t("Disabled outside login")}
                  />
                  <RadioButton
                    name={"quote_requests_visibility"}
                    value="hidden"
                    theref={register({ required: true })}
                    optionTitle={t("Hidden everywhere")}
                  />
                </RadioButtonContainer>
              ) : (
                <SampleRequestStatus>
                  {getHumanReadableTextFromUnion.get(quote_requests_visibility)}
                </SampleRequestStatus>
              )}
            </TextContainer>
          </div>
          {editMode && (
            <PrimaryButtonMedium type="submit">
              {t("Save your changes")}
            </PrimaryButtonMedium>
          )}
        </Form>
      </BorderCard>
    </Wrapper>
  );
};
